import { useEffect, useState } from 'react';

// Characters revealed per frame is a share of the backlog, so the reveal
// catches up quickly when the backend delivers large chunks.
const MIN_CHARS_PER_FRAME = 2;
const BACKLOG_DIVISOR = 12;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Reveals `text` progressively (typewriter effect) while a message streams.
 * Animates only when the message was streaming on mount; messages that are
 * final on mount, and users preferring reduced motion, get the full text at
 * once. After `streaming` turns false the remainder is still revealed.
 */
export const useRevealedText = (text: string, streaming: boolean): string => {
  const [animate] = useState(() => streaming && !prefersReducedMotion());
  const [shown, setShown] = useState(0);
  const length = text.length;

  useEffect(() => {
    if (!animate || shown >= length) return undefined;
    const frame = requestAnimationFrame(() => {
      setShown((current) => {
        const backlog = length - current;
        const step = Math.max(
          MIN_CHARS_PER_FRAME,
          Math.ceil(backlog / BACKLOG_DIVISOR)
        );
        return Math.min(length, current + step);
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [animate, shown, length]);

  return animate ? text.slice(0, shown) : text;
};
